/**
 * FavoritesRepository — HTTP access for `/favorites` (per-user favorites store,
 * backed by Elasticsearch on the backend).
 *
 * Endpoints:
 *   GET    /favorites          → { items: Favorite[] }
 *   PUT    /favorites/:id      → Favorite
 *   DELETE /favorites/:id      → (no body)
 *
 * Conventions:
 * - The client (`@/repositories/clients/AuthenticatedClient`) is loaded LAZILY
 *   at call-time via `getClient()`. This is intentional: a top-level import
 *   pulls in the global i18n instance (and `createI18n` from `vue-i18n`) at
 *   module-eval time, which breaks tests that mock `vue-i18n` without exporting
 *   `createI18n` but still need to import this module (or eager consumers like
 *   `FavoriteToggle`) eagerly. Do NOT switch this to a top-level import.
 * - `Client`'s baseURL already includes the `/api/v2` prefix.
 * - Methods return the raw axios response so the service can read the
 *   `.data` shape (`.data.items`, `.data`) and run its optimistic-update logic.
 */
const FAVORITES_PATH = '/favorites';

async function getClient() {
  const mod = await import('@/repositories/clients/AuthenticatedClient');
  return mod.default;
}

export default {
  /**
   * List all favorites for the current user.
   * @returns {Promise<import('axios').AxiosResponse>} raw response; `.data.items` is the array.
   */
  async list() {
    const Client = await getClient();
    return Client.get(FAVORITES_PATH);
  },

  /**
   * Add or update a favorite by id.
   * @param {string} id
   * @param {{ kind: string, label: string, to: string, icon?: string|null }} payload
   * @returns {Promise<import('axios').AxiosResponse>} raw response; `.data` is the persisted favorite.
   */
  async put(id, payload) {
    const Client = await getClient();
    return Client.put(`${FAVORITES_PATH}/${encodeURIComponent(id)}`, payload);
  },

  /**
   * Remove a favorite by id.
   * @param {string} id
   * @returns {Promise<import('axios').AxiosResponse>} raw response.
   */
  async remove(id) {
    const Client = await getClient();
    return Client.delete(`${FAVORITES_PATH}/${encodeURIComponent(id)}`);
  },
};
